import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterState } from "@/components/feature/FilterBar";
import WorkCard from "@/components/feature/WorkCard";
import { allWorks, applyFilter, searchWorks, sortWorks } from "@/utils/media";
import { usePageSeo } from "@/hooks/usePageSeo";

const defaultFilter: FilterState = {
  region: "全部",
  year: "全部",
  language: "全部",
  genre: "全部",
  sort: "rating",
};

const hotPicks = [...allWorks].sort((a, b) => b.rating - a.rating).slice(0, 6);

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [draft, setDraft] = useState(keyword);
  const [filter, setFilter] = useState<FilterState>(defaultFilter);

  useEffect(() => {
    setDraft(keyword);
    setFilter(defaultFilter);
  }, [keyword]);

  const matches = useMemo(() => searchWorks(keyword), [keyword]);
  const resultList = useMemo(
    () => sortWorks(applyFilter(matches, filter), filter.sort),
    [matches, filter],
  );

  usePageSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 91av视频` : "搜索影视 - 91av视频",
    description: `在 91av视频搜索“${keyword || "影视资源"}”，查看匹配的影视作品列表、评分与详情信息。`,
    keywords: `91av视频,影视搜索,${keyword || "影视资源"},在线视频`,
  });

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    setParams({ q });
  };

  const patch = (next: Partial<FilterState>) => setFilter((prev) => ({ ...prev, ...next }));
  const reset = () => setFilter(defaultFilter);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mt-5 rounded-lg border border-background-200/70 bg-background-100/60 p-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {keyword ? `“${keyword}”的搜索结果` : "搜索影视资源"}
        </h1>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300/70 bg-background-50 px-4 py-3 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              type="search"
              placeholder="输入影片名称、演员或题材关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>
        <p className="mt-3 text-sm text-foreground-600">
          关键词匹配结果：
          <strong className="mx-1 text-primary-700">{matches.length}</strong>
          部作品，筛选后显示
          <strong className="mx-1 text-accent-600">{resultList.length}</strong>部
        </p>
      </div>

      {matches.length > 0 ? (
        <>
          <div className="mt-5">
            <FilterBar value={filter} onChange={patch} onReset={reset} resultCount={resultList.length} />
          </div>

          {resultList.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5 xl:grid-cols-6">
              {resultList.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
              <i className="ri-filter-off-line text-5xl text-foreground-400" />
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                筛选后没有匹配结果
              </h2>
              <p className="mt-2 text-sm text-foreground-600">试试重置筛选条件，查看全部匹配作品。</p>
              <button
                type="button"
                onClick={reset}
                className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6">
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              没有找到与“{keyword}”相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，比如影片名、演员名或题材（科幻、古装、美食等）。
            </p>
            <Link
              to="/"
              className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>

          <section className="mt-10">
            <div className="mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-xl text-accent-600" />
              <h2 className="font-heading text-lg font-bold text-foreground-950">为你推荐热门影视</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {hotPicks.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}